import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Card, Checkbox, Col, Descriptions, Divider, Row, Select, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_user_info } from '../../../redux/actions/usersActions';
import { get_workspaces } from '../../../redux/actions/organizationActions';
import {
	get_depot_info, get_depots,
	get_group_info, get_groups,
	get_region_info, get_regions,
	get_zone_info, get_zones
} from '../../../redux/actions/areasActions';
import { staff_update } from '../../../redux/actions/staffsActions';

// Utils
import isEmpty from '../../../utils/isEmpty';
import { get_staff_type_desc } from '../../../utils/get_status_desc';
import { create_select_options } from '../../../utils/create_select_options';

// Acciones de lista e info por nivel de área
const LEVEL_ACTIONS = { region: get_regions, zone: get_zones, depot: get_depots, group: get_groups };
const LEVEL_INFO = { region: get_region_info, zone: get_zone_info, depot: get_depot_info, group: get_group_info };
const LEVEL_LIST_KEY = { region: 'regions', zone: 'zones', depot: 'depots', group: 'groups' };

// Niveles de cascada según el staff_type (potencias de 2). General(1)/Workspace(2) => sin áreas.
const cascadeLevels = (staffType) => ({
	4: ['region'],
	8: ['region', 'zone'],
	16: ['region', 'zone', 'depot'],
	32: ['region', 'zone', 'depot', 'group']
}[staffType] ?? []);

const emptyCascade = () => ({
	region: null, zone: null, depot: null, group: null,
	regionOptions: [], zoneOptions: [], depotOptions: [], groupOptions: [],
	loading: false
});

const StaffInfo = (props) => {
	const { messageApi, userStaff } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { workspaces } = useSelector(state => state.organization);
	const { user_info } = useSelector(state => state.users);

	const [staffType, setStaffType] = useState(null);
	const [organization, setOrganization] = useState('');
	// Cascada independiente por workspace marcado: { [wsOid]: { region, zone, depot, group, *Options, loading } }
	const [cascades, setCascades] = useState({});

	const LEVEL_LABEL = {
		region: t('filters.region'),
		zone: t('filters.zone'),
		depot: t('filters.depot'),
		group: t('filters.group')
	};

	// Se construye en el render (no a nivel de módulo) para que i18next ya esté listo
	const staffTypeOptions = [
		{ value: 1, label: get_staff_type_desc(1) },   // GENERAL
		{ value: 2, label: get_staff_type_desc(2) },   // WORKSPACE
		{ value: 4, label: get_staff_type_desc(4) },   // REGION
		{ value: 8, label: get_staff_type_desc(8) },   // ZONE
		{ value: 16, label: get_staff_type_desc(16) }, // DEPOT
		{ value: 32, label: get_staff_type_desc(32) }  // GROUP
	];

	// --- Helpers ---------------------------------------------------------------

	// Orden alfabético/natural por label. `numeric: true` ordena números embebidos
	// correctamente (1, 2, 4, 5, 11 en vez del lexicográfico 1, 11, 2, 4, 5).
	const sortByLabel = (options) =>
		[...options].sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { numeric: true, sensitivity: 'base' }));

	// Trae las opciones de un nivel para un workspace (usa el valor de retorno de la
	// acción, no el slice global de redux, para no pisar la cascada de otros workspaces).
	const fetchLevelOptions = (level, org, wsOid, extra = {}) =>
		dispatch(LEVEL_ACTIONS[level]({ organization: org, workspace: wsOid, ...extra }))
			.then((data) => sortByLabel(create_select_options(data?.[LEVEL_LIST_KEY[level]] ?? [], 'name')));

	const patchCascade = (wsOid, patch) => {
		setCascades((prev) => (
			prev[wsOid] ? { ...prev, [wsOid]: { ...prev[wsOid], ...patch } } : prev
		));
	};

	const workspaceLabel = (wsOid) =>
		create_select_options(workspaces, 'name').find((o) => o.value === wsOid)?.label ?? wsOid;

	// --- Prefill (edición de un supervisor existente) --------------------------

	useEffect(() => {
		if (isEmpty(userStaff)) return;

		const org = userStaff?.organization?.$oid;
		const st = userStaff?.staff_type;

		setOrganization(org);
		setStaffType(st);
		dispatch(get_user_info(userStaff?.user?.$oid));
		dispatch(get_workspaces({ organization: org }));

		const wsOids = (userStaff?.workspaces ?? []).map((w) => w?.$oid).filter(Boolean);

		const initial = {};
		wsOids.forEach((ws) => { initial[ws] = emptyCascade(); });
		setCascades(initial);

		const levels = cascadeLevels(st);
		if (levels.length === 0) return; // General/Workspace: sin áreas

		// Trae las regiones de cada workspace (para emparejar áreas guardadas y mostrarlas)
		Promise.all(wsOids.map((ws) => fetchLevelOptions('region', org, ws).then((opts) => ({ ws, opts }))))
			.then((results) => {
				const regionOptionsByWs = {};
				results.forEach(({ ws, opts }) => { regionOptionsByWs[ws] = opts; });

				setCascades((prev) => {
					const next = { ...prev };
					wsOids.forEach((ws) => {
						if (next[ws]) next[ws] = { ...next[ws], regionOptions: regionOptionsByWs[ws] ?? [] };
					});
					return next;
				});

				prefillAreas(st, org, levels, regionOptionsByWs);
			});
	}, [userStaff]);

	// Empareja cada área guardada (nivel final = staff_type) con su workspace vía la región padre
	const prefillAreas = (st, org, levels, regionOptionsByWs) => {
		const finalLevel = levels[levels.length - 1];
		const savedOids = (userStaff?.[LEVEL_LIST_KEY[finalLevel]] ?? []).map((a) => a?.$oid).filter(Boolean);

		const findWsByRegion = (regionOid) =>
			Object.keys(regionOptionsByWs).find((ws) =>
				(regionOptionsByWs[ws] ?? []).some((o) => o.value === regionOid)
			);

		// Niveles intermedios (Selects) = todos menos el final (checkboxes)
		const intermediateLevels = levels.slice(0, -1);

		savedOids.forEach((oid) => {
			// Nivel final = región: emparejar directo por membresía
			if (finalLevel === 'region') {
				const ws = findWsByRegion(oid);
				if (ws) addFinalArea(ws, 'region', oid);
				return;
			}

			// Niveles profundos: usar *_info para reconstruir la jerarquía padre
			dispatch(LEVEL_INFO[finalLevel](oid)).then((info) => {
				if (!info) return;

				const regionOid = info?.region?.$oid ?? null;
				const zoneOid = info?.zone?.$oid ?? null;
				const depotOid = info?.depot?.$oid ?? null;

				const ws = findWsByRegion(regionOid);
				if (!ws) return;

				// Solo los niveles intermedios van como valor único; el final se acumula en array
				const patch = {};
				if (intermediateLevels.includes('region')) patch.region = regionOid;
				if (intermediateLevels.includes('zone')) patch.zone = zoneOid;
				if (intermediateLevels.includes('depot')) patch.depot = depotOid;
				patchCascade(ws, patch);
				addFinalArea(ws, finalLevel, oid);

				// Trae las opciones intermedias para que los Select muestren el valor seleccionado
				if (levels.includes('zone') && regionOid) {
					fetchLevelOptions('zone', org, ws, { region: regionOid }).then((opts) => patchCascade(ws, { zoneOptions: opts }));
				}
				if (levels.includes('depot') && zoneOid) {
					fetchLevelOptions('depot', org, ws, { zone: zoneOid }).then((opts) => patchCascade(ws, { depotOptions: opts }));
				}
				if (levels.includes('group') && depotOid) {
					fetchLevelOptions('group', org, ws, { depot: depotOid }).then((opts) => patchCascade(ws, { groupOptions: opts }));
				}
			});
		});
	};

	// --- Handlers --------------------------------------------------------------

	const onStaffTypeChange = (value) => {
		setStaffType(value);

		const wsOids = Object.keys(cascades);
		const reset = {};
		wsOids.forEach((ws) => { reset[ws] = emptyCascade(); });
		setCascades(reset);

		if (cascadeLevels(value).includes('region')) {
			wsOids.forEach((ws) => {
				patchCascade(ws, { loading: true });
				fetchLevelOptions('region', organization, ws)
					.then((opts) => patchCascade(ws, { regionOptions: opts, loading: false }));
			});
		}
	};

	const onWorkspacesChange = (values) => {
		const added = values.filter((ws) => !cascades[ws]);

		setCascades((prev) => {
			const next = {};
			values.forEach((ws) => { next[ws] = prev[ws] ?? emptyCascade(); });
			return next;
		});

		if (cascadeLevels(staffType).includes('region')) {
			added.forEach((ws) => {
				patchCascade(ws, { loading: true });
				fetchLevelOptions('region', organization, ws)
					.then((opts) => patchCascade(ws, { regionOptions: opts, loading: false }));
			});
		}
	};

	const onCascadeChange = (wsOid, level, value) => {
		const levels = cascadeLevels(staffType);
		const idx = levels.indexOf(level);
		const childLevels = levels.slice(idx + 1);

		// Setea este nivel y limpia todos sus hijos (valor + opciones)
		const patch = { [level]: value ?? null };
		childLevels.forEach((child) => {
			patch[child] = null;
			patch[`${child}Options`] = [];
		});
		patchCascade(wsOid, patch);

		// Trae las opciones del nivel hijo inmediato
		const childLevel = levels[idx + 1];
		if (childLevel && value) {
			patchCascade(wsOid, { loading: true });
			fetchLevelOptions(childLevel, organization, wsOid, { [level]: value })
				.then((opts) => patchCascade(wsOid, { [`${childLevel}Options`]: opts, loading: false }));
		}
	};

	// Nivel final = checkboxes: Checkbox.Group ya entrega el array resultante (push/pop)
	const onFinalAreasChange = (wsOid, level, values) => {
		patchCascade(wsOid, { [level]: values });
	};

	// Agrega un oid al array del nivel final de un workspace (usado en el prefill)
	const addFinalArea = (wsOid, level, oid) => {
		setCascades((prev) => {
			if (!prev[wsOid]) return prev;
			const arr = prev[wsOid][level] ?? [];
			if (arr.includes(oid)) return prev;
			return { ...prev, [wsOid]: { ...prev[wsOid], [level]: [...arr, oid] } };
		});
	};

	// --- Submit ----------------------------------------------------------------

	const onFinish = () => {
		const levels = cascadeLevels(staffType);
		const finalLevel = levels[levels.length - 1];
		// El nivel final se guarda como array por workspace (checkboxes) => se aplanan
		const areasOf = (lvl) => Object.values(cascades).flatMap((c) => c[lvl] ?? []);

		const body = {
			email: '',
			username: user_info?.username,
			status: 0,
			permissions_type: [userStaff?.permissions?.$oid],
			organization,
			staff_type: staffType,
			presets: userStaff?.presets !== undefined ? userStaff?.presets?.map((preset) => preset?.$oid) : [],
			actions: [],
			workspaces: Object.keys(cascades),
			regions: finalLevel === 'region' ? areasOf('region') : [],
			zones: finalLevel === 'zone' ? areasOf('zone') : [],
			depots: finalLevel === 'depot' ? areasOf('depot') : [],
			groups: finalLevel === 'group' ? areasOf('group') : [],
			routes: [],
			locations: []
		};

		dispatch(staff_update(messageApi, userStaff?._id?.$oid, body));
	};

	// --- Render ----------------------------------------------------------------

	const workspaceOptions = create_select_options(workspaces, 'name');
	const selectedWorkspaces = Object.keys(cascades);
	const levels = cascadeLevels(staffType);

	const renderCascade = (wsOid) => {
		const cascade = cascades[wsOid];
		if (!cascade) return null;

		const finalLevel = levels[levels.length - 1];
		const intermediateLevels = levels.slice(0, -1);
		const finalParent = intermediateLevels[intermediateLevels.length - 1]; // undefined si el final no tiene padre
		const finalDisabled = finalParent && !cascade[finalParent];
		const finalOptions = cascade[`${finalLevel}Options`] ?? [];
		const finalSelected = cascade[finalLevel] ?? [];

		return (
			<div key={wsOid} style={{ marginBottom: '1.5rem' }}>
				<Typography.Text strong>
					{workspaceLabel(wsOid)}
				</Typography.Text>
				{cascade.loading && <Spin size='small' style={{ marginLeft: '0.75rem' }} />}

				{/* Niveles intermedios: Selects que van filtrando */}
				{intermediateLevels.length > 0 &&
					<Row gutter={[16, 16]} style={{ marginTop: '0.5rem' }}>
						{intermediateLevels.map((level, i) => {
							const parentLevel = intermediateLevels[i - 1];
							const disabled = i > 0 && !cascade[parentLevel];

							return (
								<Col key={level} xs={24} sm={12} md={12} lg={8} xl={8}>
									<Typography.Text type='secondary'>{LEVEL_LABEL[level]}</Typography.Text>
									<Select
										style={{ width: '100%', marginTop: '0.25rem' }}
										showSearch
										allowClear
										disabled={disabled}
										placeholder={`Selecciona ${LEVEL_LABEL[level]}`}
										optionFilterProp='label'
										value={cascade[level] ?? undefined}
										onChange={(val) => onCascadeChange(wsOid, level, val ?? null)}
										filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
										options={cascade[`${level}Options`]}
									/>
								</Col>
							);
						})}
					</Row>
				}

				{/* Nivel final: checkboxes con todas las opciones (selección múltiple) */}
				<div style={{ marginTop: intermediateLevels.length > 0 ? '1rem' : '0.5rem' }}>
					<Typography.Text type='secondary'>{LEVEL_LABEL[finalLevel]}</Typography.Text>
					{finalDisabled
						?	<div><Typography.Text type='secondary' italic>Selecciona {LEVEL_LABEL[finalParent]} para ver las opciones</Typography.Text></div>
						:	finalOptions.length === 0
							?	<div><Typography.Text type='secondary' italic>No hay opciones disponibles</Typography.Text></div>
							:	<Checkbox.Group
									style={{ width: '100%', marginTop: '0.5rem' }}
									value={finalSelected}
									onChange={(values) => onFinalAreasChange(wsOid, finalLevel, values)}
								>
									<Row gutter={[16, 16]} align='middle'>
										{finalOptions.map((opt) => (
											<Col
												key={opt.value}
												xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}
											>
												<Checkbox value={opt.value}>{opt.label}</Checkbox>
											</Col>
										))}
									</Row>
								</Checkbox.Group>
					}
				</div>
			</div>
		);
	};

	return (
		<Card bordered={false}>
			<Typography.Title
				level={3}
				copyable={{
					text: user_info?._id?.$oid,
					tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')],
				}}
			>
				{user_info?.name}
			</Typography.Title>
			<Descriptions
				items={[
					{
						key: 'username',
						label: 'Nombre de usuario:',
						children: user_info?.username
					},
					{
						key: 'date',
						label: 'Fecha de creacion:',
						children: new Date(user_info?.date?.$date).toLocaleString(localStorage.getItem('i18nextLng'), { hour12: true })
					}
				]}
			/>
			<Divider />
			<Typography.Title level={5}>Permisos</Typography.Title>

			{/* Tipo de supervisor */}
			<Typography.Text strong>Tipo de supervisor</Typography.Text>
			<Select
				style={{ width: '100%', marginTop: '0.5rem' }}
				showSearch
				placeholder='Selecciona un tipo de supervisor'
				optionFilterProp='label'
				value={staffType ?? undefined}
				onChange={onStaffTypeChange}
				filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
				options={staffTypeOptions}
			/>

			<Divider />

			{/* Workspaces (selección múltiple por checkboxes) */}
			<Typography.Title level={5}>Workspaces</Typography.Title>
			<Checkbox.Group
				style={{ width: '100%' }}
				value={selectedWorkspaces}
				onChange={onWorkspacesChange}
			>
				<Row gutter={[16, 16]} align='middle'>
					{workspaceOptions.map((option) => (
						<Col
							key={option.value}
							xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}
						>
							<Checkbox value={option.value}>
								{option.label}
							</Checkbox>
						</Col>
					))}
				</Row>
			</Checkbox.Group>

			{/* Cascada de áreas por cada workspace marcado (según el nivel del staff_type) */}
			{levels.length > 0 && selectedWorkspaces.length > 0 &&
				<>
					<Divider />
					<Typography.Title level={5}>{t('filters.areas')}</Typography.Title>
					{selectedWorkspaces.map((wsOid, i) => (
						<React.Fragment key={wsOid}>
							{i > 0 && <Divider dashed />}
							{renderCascade(wsOid)}
						</React.Fragment>
					))}
				</>
			}

			<Divider />
			<Button
				type='primary'
				block
				onClick={onFinish}
			>
				{t('locations.finishChanges')}
			</Button>
		</Card>
	)
}

StaffInfo.propTypes = {
	messageApi: PropTypes.object.isRequired,
	userStaff: PropTypes.object.isRequired
}

export default StaffInfo;
